import React, { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Loader2, 
  ShoppingCart, 
  CalendarIcon, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  Clock
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCreateSale, PaymentWithMeta } from "@/hooks/useCreateSale";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface EditableInstallment {
  number: number;
  date: string; // yyyy-MM-dd internally
  amount: number;
  isDownPayment: boolean;
  // Step 2 fields
  markedAsPaid: boolean;
  paymentMethod?: string;
  paymentDate?: string;
  reference?: string;
}

interface RegisterSaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  onSuccess?: () => void;
}

interface CatalogService {
  id: string;
  name: string;
  base_price: number;
  description?: string;
  pricing_type?: 'flat' | 'per_person' | 'variable';
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'wire', label: 'Transferência' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'check', label: 'Cheque' },
];

// Helper to format date as dd/MM/yyyy for display
const formatDateBR = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return safeFormat(date, 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return dateStr;
  }
};

// Helper to parse dd/MM/yyyy to yyyy-MM-dd
const parseDateBR = (displayDate: string): string => {
  if (!displayDate) return '';
  try {
    // If already in yyyy-MM-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(displayDate)) {
      return displayDate;
    }
    const parsed = parse(displayDate, 'dd/MM/yyyy', new Date());
    return safeFormat(parsed, 'yyyy-MM-dd');
  } catch {
    return displayDate;
  }
};

export default function RegisterSaleModal({
  open,
  onOpenChange,
  contactId,
  onSuccess,
}: RegisterSaleModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [services, setServices] = useState<CatalogService[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [discountPerUnit, setDiscountPerUnit] = useState(0);
  const [installments, setInstallments] = useState("1");
  const [downPayment, setDownPayment] = useState<number>(0);
  const [firstDueDate, setFirstDueDate] = useState(() => {
    return safeFormat(new Date(), 'yyyy-MM-dd');
  });

  const { createStandaloneSale } = useCreateSale();

  const selectedService = services.find((s) => s.id === selectedServiceId);
  const basePrice = selectedService?.base_price || 0;
  const isVariable = selectedService?.pricing_type === 'variable';
  
  // Calculate totals
  const pricePerUnitAfterDiscount = Math.max(0, basePrice - discountPerUnit);
  const subtotal = basePrice * quantity;
  const totalDiscount = discountPerUnit * quantity;
  const totalAmount = pricePerUnitAfterDiscount * quantity;

  useEffect(() => {
    if (open) {
      loadServices();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setStep(1);
    setSelectedServiceId("");
    setQuantity(1);
    setDiscountPerUnit(0);
    setInstallments("1");
    setDownPayment(0);
    setFirstDueDate(safeFormat(new Date(), 'yyyy-MM-dd'));
    setCustomInstallments([]);
  };

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("catalog")
        .select("id, name, base_price, description, pricing_type")
        .eq("type", "service")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error("Error loading services:", error);
      toast.error("Erro ao carregar serviços");
    }
    setLoading(false);
  };

  const handleServiceChange = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    // Reset quantity to 1 when changing service
    setQuantity(1);
    setDiscountPerUnit(0);
  };

  // Editable installments state
  const [customInstallments, setCustomInstallments] = useState<EditableInstallment[]>([]);

  // Generate installments when params change (only in step 1)
  useEffect(() => {
    if (step !== 1) return;
    if (totalAmount <= 0 || parseInt(installments) <= 0) {
      setCustomInstallments([]);
      return;
    }

    const dates: EditableInstallment[] = [];
    const baseDate = new Date(firstDueDate + "T12:00:00");
    const numInstallments = parseInt(installments);
    
    if (downPayment > 0) {
      dates.push({
        number: 1,
        date: safeFormat(new Date(), 'yyyy-MM-dd'),
        amount: downPayment,
        isDownPayment: true,
        markedAsPaid: false,
      });
    }

    const remaining = totalAmount - downPayment;
    const count = downPayment > 0 ? numInstallments - 1 : numInstallments;
    
    if (count > 0) {
      const installmentValue = remaining / count;
      const roundedInstallment = Math.floor(installmentValue * 100) / 100;

      for (let i = 0; i < count; i++) {
        const dueDate = new Date(baseDate);
        dueDate.setMonth(dueDate.getMonth() + i);
        
        // Last installment absorbs the rounding difference
        const isLast = i === count - 1;
        let amount = roundedInstallment;
        
        if (isLast) {
          const previousSum = roundedInstallment * (count - 1);
          amount = Math.round((remaining - previousSum) * 100) / 100;
        }
        
        dates.push({
          number: downPayment > 0 ? i + 2 : i + 1,
          date: safeFormat(dueDate, 'yyyy-MM-dd'),
          amount: amount,
          isDownPayment: false,
          markedAsPaid: false,
        });
      }
    }

    setCustomInstallments(dates);
  }, [totalAmount, installments, downPayment, firstDueDate, step]);

  const handleInstallmentDateChange = (index: number, newDate: string) => {
    // Accepts yyyy-MM-dd format directly
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { ...inst, date: newDate } : inst)
    );
  };

  const handleInstallmentAmountChange = (index: number, amount: number) => {
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { ...inst, amount: Math.max(0, amount) } : inst)
    );
  };

  const handleMarkAsPaid = (index: number, checked: boolean) => {
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { 
        ...inst, 
        markedAsPaid: checked,
        paymentMethod: checked ? (inst.paymentMethod || 'cash') : undefined,
        paymentDate: checked ? (inst.paymentDate || safeFormat(new Date(), 'yyyy-MM-dd')) : undefined,
      } : inst)
    );
  };

  const handlePaymentMethodChange = (index: number, method: string) => {
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { ...inst, paymentMethod: method } : inst)
    );
  };

  const handlePaymentDateChange = (index: number, date: string) => {
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { ...inst, paymentDate: date } : inst)
    );
  };

  const handleReferenceChange = (index: number, ref: string) => {
    setCustomInstallments(prev =>
      prev.map((inst, i) => i === index ? { ...inst, reference: ref } : inst)
    );
  };

  const installmentsTotal = customInstallments.reduce((sum, inst) => sum + inst.amount, 0);
  const hasAmountMismatch = Math.abs(installmentsTotal - totalAmount) > 0.01;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const handleAdvanceToStep2 = () => {
    if (!selectedServiceId) {
      toast.error("Selecione um serviço");
      return;
    }
    if (totalAmount <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (customInstallments.length === 0) {
      toast.error("Configure as parcelas");
      return;
    }
    if (hasAmountMismatch) {
      toast.error("A soma das parcelas deve ser igual ao valor total");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const installmentsToSave: PaymentWithMeta[] = customInstallments.map(inst => ({
        date: inst.date,
        amount: inst.amount,
        isDownPayment: inst.isDownPayment,
        markedAsPaid: inst.markedAsPaid,
        paymentMethod: inst.paymentMethod,
        paymentDate: inst.paymentDate,
        reference: inst.reference,
      }));

      const result = await createStandaloneSale({
        contactId,
        catalogId: selectedServiceId,
        totalAmount,
        customInstallments: installmentsToSave,
        userId: user?.id,
      });

      toast.success("Venda registrada com sucesso!");
      onOpenChange(false);
      onSuccess?.();
      
      // Navigate to the created deal
      if (result.dealId) {
        navigate(`/deal/${result.dealId}`);
      }
    } catch (error: any) {
      console.error("Error creating sale:", error);
      toast.error(error.message || "Erro ao registrar venda");
    }
    setSaving(false);
  };

  const markedCount = customInstallments.filter(i => i.markedAsPaid).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Registrar Venda
            {step === 2 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                - Etapa 2: Pagamentos
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? "Configure o serviço e as condições de pagamento."
              : "Revise as parcelas e marque as que já foram pagas."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : step === 1 ? (
          // STEP 1: Configure Sale
          <div className="space-y-4 py-4">
            {/* Service selection */}
            <div className="space-y-2">
              <Label htmlFor="service">Serviço</Label>
              <Select
                value={selectedServiceId}
                onValueChange={handleServiceChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um serviço" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} - {formatCurrency(service.base_price)}
                      {service.pricing_type === 'variable' && '/unidade'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedService && (
              <>
                {/* Base price display (non-editable) */}
                <div className="space-y-2">
                  <Label>Preço Base {isVariable && '(por unidade)'}</Label>
                  <div className="h-10 px-3 py-2 rounded-md border border-input bg-muted text-sm font-medium">
                    {formatCurrency(basePrice)}
                  </div>
                </div>

                {/* Quantity field - only for variable products */}
                {isVariable && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantidade</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      step="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                  </div>
                )}

                {/* Discount per unit */}
                <div className="space-y-2">
                  <Label htmlFor="discount">
                    Desconto {isVariable ? 'por unidade' : ''} (opcional)
                  </Label>
                  <Input
                    id="discount"
                    type="number"
                    min="0"
                    step="0.01"
                    max={basePrice}
                    value={discountPerUnit}
                    onChange={(e) => setDiscountPerUnit(Math.min(basePrice, parseFloat(e.target.value) || 0))}
                    placeholder="0.00"
                  />
                </div>

                {/* Pricing summary */}
                <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
                  {isVariable && (
                    <>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Subtotal ({quantity}x {formatCurrency(basePrice)}):</span>
                        <span>{formatCurrency(subtotal)}</span>
                      </div>
                      {totalDiscount > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Desconto ({quantity}x {formatCurrency(discountPerUnit)}):</span>
                          <span className="text-destructive">-{formatCurrency(totalDiscount)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {!isVariable && discountPerUnit > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Desconto:</span>
                      <span className="text-destructive">-{formatCurrency(discountPerUnit)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-foreground pt-1 border-t border-border">
                    <span>Valor Total:</span>
                    <span>{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </>
            )}

            {/* Payment configuration */}
            {totalAmount > 0 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="installments">Número de Parcelas</Label>
                  <Select value={installments} onValueChange={setInstallments}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                        <SelectItem key={n} value={n.toString()}>
                          {n}x
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="downPayment">Entrada (opcional)</Label>
                  <Input
                    id="downPayment"
                    type="number"
                    min="0"
                    step="0.01"
                    max={totalAmount}
                    value={downPayment}
                    onChange={(e) => setDownPayment(Math.min(totalAmount, parseFloat(e.target.value) || 0))}
                    placeholder="Valor da entrada"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Data da Primeira Parcela</Label>
                  <DatePickerInput
                    value={firstDueDate || null}
                    onChange={(date) => date && setFirstDueDate(safeFormat(date, 'yyyy-MM-dd'))}
                    placeholder="dd/mm/aaaa"
                  />
                </div>
              </>
            )}

            {/* Editable installments list */}
            {totalAmount > 0 && customInstallments.length > 0 && (
              <div className="bg-muted rounded-lg p-4 text-sm space-y-3">
                <p className="font-medium text-foreground">
                  Parcelas ({customInstallments.length})
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {customInstallments.map((inst, index) => (
                    <div key={inst.number} className="flex items-center gap-2 p-2 bg-background border border-border rounded-md">
                      <span className="text-xs text-muted-foreground w-20 shrink-0">
                        {inst.number}ª {inst.isDownPayment ? '(Entrada)' : 'parcela'}
                      </span>
                      
                      {/* Editable date */}
                      <DatePickerInput
                        className="w-36"
                        value={inst.date || null}
                        onChange={(date) => date && handleInstallmentDateChange(index, safeFormat(date, 'yyyy-MM-dd'))}
                      />
                      
                      {/* Editable amount */}
                      <div className="relative flex-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={inst.amount}
                          onChange={(e) => handleInstallmentAmountChange(index, parseFloat(e.target.value) || 0)}
                          className="h-8 text-xs text-right pl-5 pr-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total with validation */}
                <div className="pt-2 border-t border-border flex justify-between items-center">
                  <span className="text-muted-foreground">Total das parcelas:</span>
                  <span className={cn(
                    "font-medium",
                    hasAmountMismatch ? "text-destructive" : "text-foreground"
                  )}>
                    {formatCurrency(installmentsTotal)}
                    {!hasAmountMismatch && <span className="ml-1 text-primary">✓</span>}
                  </span>
                </div>
                {hasAmountMismatch && (
                  <p className="text-xs text-destructive">
                    Diferença: {formatCurrency(Math.abs(installmentsTotal - totalAmount))} 
                    {installmentsTotal > totalAmount ? ' a mais' : ' a menos'} 
                    (esperado: {formatCurrency(totalAmount)})
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          // STEP 2: Manage Payments
          <div className="space-y-4 py-4">
            {/* Summary header */}
            <div className="bg-muted rounded-lg p-3 text-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-foreground">{selectedService?.name}</p>
                  <p className="text-muted-foreground text-xs">{customInstallments.length} parcela(s)</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-foreground">{formatCurrency(totalAmount)}</p>
                  {markedCount > 0 && (
                    <p className="text-xs text-primary">{markedCount} marcada(s) como paga</p>
                  )}
                </div>
              </div>
            </div>

            {/* Installments list */}
            <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
              {customInstallments.map((inst, index) => (
                <div 
                  key={inst.number}
                  className={cn(
                    "border rounded-lg p-3 space-y-3 transition-colors",
                    inst.markedAsPaid ? "border-primary/50 bg-primary/5" : "border-border"
                  )}
                >
                  {/* Row header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {inst.markedAsPaid ? (
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                      ) : (
                        <Clock className="w-5 h-5 text-muted-foreground" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {inst.number}ª {inst.isDownPayment ? 'Entrada' : 'Parcela'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Vence: {formatDateBR(inst.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatCurrency(inst.amount)}</p>
                    </div>
                  </div>

                  {/* Mark as paid checkbox */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`paid-${index}`}
                      checked={inst.markedAsPaid}
                      onCheckedChange={(checked) => handleMarkAsPaid(index, !!checked)}
                    />
                    <label
                      htmlFor={`paid-${index}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Marcar como pago (pendente revisão)
                    </label>
                  </div>

                  {/* Payment details (only when marked as paid) */}
                  {inst.markedAsPaid && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                      <div className="space-y-1">
                        <Label className="text-xs">Método</Label>
                        <Select 
                          value={inst.paymentMethod || 'cash'} 
                          onValueChange={(v) => handlePaymentMethodChange(index, v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map((m) => (
                              <SelectItem key={m.value} value={m.value}>
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Data Pgto</Label>
                        <DatePickerInput
                          value={inst.paymentDate || null}
                          onChange={(date) => date && handlePaymentDateChange(index, safeFormat(date, 'yyyy-MM-dd'))}
                          placeholder="Hoje"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Referência (opcional)</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="Nº transação, comprovante..."
                          value={inst.reference || ''}
                          onChange={(e) => handleReferenceChange(index, e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Info note */}
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-200">
              <p><strong>Nota:</strong> Pagamentos marcados ficarão com status "Pendente de Revisão" até serem confirmados.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAdvanceToStep2} 
                disabled={loading || !selectedServiceId || hasAmountMismatch}
              >
                Avançar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Finalizar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
